import {AfterViewInit, Component, OnDestroy, OnInit} from '@angular/core';
import Player from '@vimeo/player';
import {CultureModel} from '../../../models/localization-and-pricing/culture.model';
import {NavigationEnd, Router} from '@angular/router';
import {DomSanitizer, SafeResourceUrl, Title} from '@angular/platform-browser';
import {CountryModel} from '../../../models/localization-and-pricing/country.model';
import {NavigationModel} from '../../../models/localization-and-pricing/navigation.model';
import {CompetitionService} from '../../../services/competition/competition.service';
import {CompetitionModel} from '../../../models/competition/competition.model';
import {COMPETITION_YEARS} from '../competitions/competition-years';
import {UserModel} from '../../../models/authentication/user.model';
import {SchoolModel} from '../../../models/school/school.model';
import {StudentModel} from '../../../models/authentication/student.model';

@Component({
  selector: 'app-landing',
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.css']
})
export class LandingComponent implements OnInit, AfterViewInit, OnDestroy {

  theoriesTab: {title: string, description: string, icon: string}[] = [
    {
      title: 'lp2.landing.researchPart.theory.reinforcementTitle',
      description: 'lp2.landing.researchPart.theory.reinforcementDescription',
      icon: 'assets/newWebsite/ReinforcementIcon.svg'
    }, {
      title: 'lp2.landing.researchPart.theory.flowTitle',
      description: 'lp2.landing.researchPart.theory.flowDescription',
      icon: 'assets/newWebsite/FlowIcon.svg'
    }, {
      title: 'lp2.landing.researchPart.theory.spacingTitle',
      description: 'lp2.landing.researchPart.theory.spacingDescription',
      icon: 'assets/newWebsite/SpacingEffectIcon.svg'
    }
  ];

  CultureModel = CultureModel;
  CountryModel = CountryModel;

  hidden: boolean;
  videoStep = 'first';
  universeUrl: SafeResourceUrl;
  howItWorksUrl: SafeResourceUrl;

  message: string;

  universeComputerDiv: HTMLElement;
  playerUniverseComputer: Player;

  universeMobileDiv: HTMLElement;
  playerUniverseMobile: Player;

  hiwComputerDiv: HTMLElement;
  playerHiwComputer: Player;

  hiwMobileDiv: HTMLElement;
  playerHiwMobile: Player;

  comp: CompetitionModel = null;
  competitionYears = COMPETITION_YEARS;
  private competitionsById: { [id: string]: CompetitionModel } = {};

  /**
   * Which competition section the landing page shows. Change this one value to switch:
   *  'v2'      - the redesigned hero with year tabs
   *  'classic' - the original section
   */
  competitionLayout: 'v2' | 'classic' = 'v2';

  /**
   * How the 'v2' competition hero looks on small screens:
   *  'playful' - the running children on the left, stats and button on the right
   *  'stacked' - everything in one centered column
   */
  competitionMobileLayout: 'playful' | 'stacked' = 'playful';
  headerBackgroundColor = 'rgba(0, 0, 0, 0.4)';
  headerTextLight = true;

  constructor(private router: Router,
              private compSv: CompetitionService,
              private titleService: Title,
              public sanitizer: DomSanitizer) {

    this.hidden = true;

    if (CultureModel.getHomepageCulture() === CultureModel.isIS) {
      this.titleService.setTitle('Skemmtileg og árangursrík leið til að læra stærðfræði! | Evolytes');
    } else if (CultureModel.getHomepageCulture() === CultureModel.enGB) {
      this.titleService.setTitle('Learn maths in a fun and engaging way! | Evolytes');
    } else if (CultureModel.getHomepageCulture() === CultureModel.frFR) {
      this.titleService.setTitle('Apprenez les mathématiques de manière amusante et stimulante! | Evolytes');
    }

  }

  ngOnInit() {
    this.loadVideoUrls();

    // The newest year is hardcoded, consumer competitions are public too and would break a "latest" query
    this.compSv.fetchPublicCompetitionById(COMPETITION_YEARS[0].id).subscribe((comp) => {

      this.comp = comp;
      this.competitionsById[comp._id] = comp;
      this.comp.startCountdown();

    });
  }

  ngAfterViewInit() {

    // Logic to reload the videos
    this.router.events.subscribe((event) => {

      if (event instanceof NavigationEnd) {
        this.loadVideoUrls();
      }

    });

    this.universeComputerDiv = document.getElementById('universeComputer');
    this.playerUniverseComputer = new Player(this.universeComputerDiv);
    this.playerUniverseComputer.setVolume(0);

    this.universeMobileDiv = document.getElementById('universeMobile');
    this.playerUniverseMobile = new Player(this.universeMobileDiv);
    this.playerUniverseMobile.setVolume(0);

    this.hiwComputerDiv = document.getElementById('needTimerComputer');
    this.playerHiwComputer = new Player(this.hiwComputerDiv);
    this.playerHiwComputer.setVolume(0);

    this.hiwMobileDiv = document.getElementById('needTimerMobile');
    this.playerHiwMobile = new Player(this.hiwMobileDiv);
    this.playerHiwMobile.setVolume(0);

    this.playerHiwComputer.on('timeupdate', ({seconds}) => {
      this.timer(seconds);
    });

    this.playerHiwMobile.on('timeupdate', ({seconds}) => {
      this.timer(seconds);
    });

    const content = document.querySelector('.landcontent');

    content.addEventListener('scroll', () => {

      if (window.innerWidth > 960 || document.documentElement.clientWidth > 960) {

        this.playUniverseComputerVideo();
        this.playHiwComputerVideo();


      } else {

        this.playUniverseMobileVideo();
        this.playHiwMobileVideo();

      }

      this.updateHeaderBackground();
    }, {
      passive: true
    });

  }

  ngOnDestroy() {
    if (this.comp) {
      this.comp.endCountdown();
    }
  }

  loadVideoUrls() {
    this.universeUrl = this.getEvolytesUniverseVideoSafeURL();
    this.howItWorksUrl = this.getHowItWorksSafeURL();
  }

  playUniverseComputerVideo() {

    // universe video handling for computer
    if (this.isInViewport(this.universeComputerDiv) === true) {

      this.playerUniverseComputer.getPaused().then((paused) => {
        if (paused === true) {
          this.playerUniverseComputer.play();
        }
      });

      this.playerUniverseMobile.getPaused().then((paused) => {
        if (paused === false) {
          this.playerUniverseMobile.pause();
        }
      });

    } else {

      this.playerUniverseComputer.getPaused().then((paused) => {
        if (paused === false) {
          this.playerUniverseComputer.pause();
        }
      });

    }

  }

  playUniverseMobileVideo() {

    // Universe video handling for mobile
    if (this.isInViewport(this.universeMobileDiv) === true) {

      this.playerUniverseMobile.getPaused().then((paused) => {
        if (paused === true) {
          this.playerUniverseMobile.play();
        }
      });

      this.playerUniverseComputer.getPaused().then((paused) => {
        if (paused === false) {
          this.playerUniverseComputer.pause();
        }
      });

    } else {

      this.playerUniverseMobile.getPaused().then((paused) => {
        if (paused === false) {
          this.playerUniverseMobile.pause();
        }
      });

    }

  }

  playHiwComputerVideo() {

    // How it works video handling for computer
    if (this.isInViewport(this.hiwComputerDiv) === true) {

      this.playerHiwComputer.getPaused().then((paused) => {
        if (paused === true) {
          this.playerHiwComputer.play();
        }
      });

      this.playerHiwMobile.getPaused().then((paused) => {
        if (paused === false) {
          this.playerHiwMobile.pause();
        }
      });

    } else {

      this.playerHiwComputer.getPaused().then((paused) => {
        if (paused === false) {
          this.playerHiwComputer.pause();
        }
      });

    }

  }

  playHiwMobileVideo() {

    if (this.isInViewport(this.hiwMobileDiv) === true) {

      this.playerHiwMobile.getPaused().then((paused) => {
        if (paused === true) {
          this.playerHiwMobile.play();
        }
      });

      this.playerHiwComputer.getPaused().then((paused) => {
        if (paused === false) {
          this.playerHiwComputer.pause();
        }
      });

    } else {

      this.playerHiwMobile.getPaused().then((paused) => {
        if (paused === false) {
          this.playerHiwMobile.pause();
        }
      });

    }

  }

  updateHeaderBackground() {
    const content = document.querySelector('.landcontent');

    // Default to transparent
    let newColor = 'rgba(0, 0, 0, 0)';

    // Get all direct children of landcontent that are top-level sections
    const allSections = Array.from(content.children) as HTMLElement[];

    allSections.forEach((section: HTMLElement) => {

      const rect = section.getBoundingClientRect();
      const contentRect = content.getBoundingClientRect();

      // Calculate the position relative to the content container
      const sectionTop = rect.top - contentRect.top;
      const sectionBottom = rect.bottom - contentRect.top;

      // Check if this section is at the top
      // Only change color when section has actually reached position 0 or is past it
      if (sectionTop <= 0 && sectionBottom > 0) {
        // Get the computed background color of this section
        const bgColor = window.getComputedStyle(section).backgroundColor;

        if (bgColor === 'rgba(84, 103, 110, 0.04)' || bgColor === 'rgba(0, 0, 0, 0)' || bgColor === 'rgb(255, 255, 255)') {
          // Black overlay
          // newColor = 'rgba(0, 0, 0, 0.3)';
          newColor = 'rgba(255, 255, 255, 1)';
          this.headerTextLight = false;
        } else {
          // newColor = 'rgba(18, 15, 10, 1)';
          newColor = 'rgba(0, 0, 0, 0.4)';
          this.headerTextLight = true;
        }
        
        // Only update if the background is not transparent
        // if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
        //   // Add 90% opacity to the background color
        //   const rgba = this.addOpacityToColor(bgColor, 0.9);
        //   newColor = rgba;
        // }
      }

    });

    this.headerBackgroundColor = newColor;
  }

  private addOpacityToColor(color: string, opacity: number): string {
    // Parse rgb/rgba color and add/modify opacity
    const rgbMatch = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)$/);
    if (rgbMatch) {
      return `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, ${opacity})`;
    }
    return color;
  }

  isInViewport(el) {
    const rect = el.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)

    );
  }

  timer(seconds) {
    if (seconds < 6) {
      this.videoStep = 'first';
    } else if (seconds < 11) {
      this.videoStep = 'second';
    } else if (seconds < 30) {
      this.videoStep = 'third';
    } else {
      this.videoStep = 'fourth';
    }
  }

  getEvolytesUniverseVideoSafeURL(): SafeResourceUrl {

    return this.sanitizer.bypassSecurityTrustResourceUrl(this.getEvolytesUniverseVideoURL());
  }

  private getEvolytesUniverseVideoURL(): string {

    let id = '';
    switch (CultureModel.getHomepageCulture()) {

      case CultureModel.enUS:
      case CultureModel.enGB:

        id = '479590933';
        break;

      case CultureModel.frFR:

        id = '724466111';
        break;

      case CultureModel.isIS:
        id = '478259532';
        break;

      default:
        id = '479590933';
        break;

    }

    const url = 'https://player.vimeo.com/video/' + id + '?badge=0&amp;muted=1&loop=1&autopause=0&amp;player_id=0&amp;app_id=58479';

    return url;

  }

  getHowItWorksSafeURL(): SafeResourceUrl {

    return this.sanitizer.bypassSecurityTrustResourceUrl(this.getHowItWorksVideoURL());
  }

  getHowItWorksVideoURL(): string {

    let id = '';
    switch (CultureModel.getHomepageCulture()) {

      case CultureModel.enUS:
      case CultureModel.enGB:

        id = '664651914';
        break;

      case CultureModel.frFR:
        id = '724514707';
        break;

      case CultureModel.isIS:
      default:
        id = '584880863';
        break;

    }

    const url = 'https://player.vimeo.com/video/' + id + '?badge=0&amp;muted=1&loop=1&autopause=0&amp;player_id=0&amp;app_id=58479';

    return url;

  }

  getStartPrice(): string {
    return CountryModel.priceStartingAt(CountryModel.getHomepageCountry());
  }

  setHidden() {
    this.hidden = !this.hidden;
  }

  // Routing

  goToSignup() {
    this.router.navigate(NavigationModel.getHomeURLPaths(['signup']));
  }

  goToSignin() {
    this.router.navigate(NavigationModel.getHomeURLPaths(['signin']));
  }

  goToFeature() {
    this.router.navigate(NavigationModel.getHomeURLPaths(['features', 'game']));
  }

  goToResearch() {
    this.router.navigate(NavigationModel.getHomeURLPaths(['research']));
  }

  goToCompetition() {
    if (this.comp) {
      let page = 'detail';
      if (this.competitionLayout === 'v2') {
        page = 'detail-v2';
      }
      this.router.navigate(NavigationModel.getHomeURLPaths(['competitions', this.comp._id, page]));
    }
  }

  /**
   * Swaps the competition shown in the hero for another year.
   */
  selectCompetitionYear(id: string) {

    if (this.comp && this.comp._id === id) {
      return;
    }

    if (this.comp) {
      this.comp.endCountdown();
    }

    if (this.competitionsById[id]) {
      this.showCompetition(this.competitionsById[id]);
      return;
    }

    this.compSv.fetchPublicCompetitionById(id).subscribe((comp) => {
      this.competitionsById[id] = comp;
      this.showCompetition(comp);
    });
  }

  private showCompetition(comp: CompetitionModel) {

    this.comp = comp;
    if (comp.isUpcoming()) {
      comp.startCountdown();
    }
  }

  navigateHome() {

    this.router.navigate(NavigationModel.getHomeURLPaths(['landing']));

  }

  navigateFeatures() {

    this.router.navigate(NavigationModel.getHomeURLPaths(['features', 'game']));

  }

  navigateSchools() {

    this.router.navigate(NavigationModel.getHomeURLPaths(['schools']));

  }

  navigatePricing() {

    this.router.navigate(NavigationModel.getHomeURLPaths(['pricing']));

  }

  login() {

    if (UserModel.isLoggedIn()) {

      if (UserModel.getCurrent().hasSchoolAccess() && SchoolModel.getCurrent() === null) {

        this.router.navigate(['selectschool']);

      } else {

        if (StudentModel.getCurrent()) {
          this.router.navigate(['platform', 'dash']);
        } else {
          this.router.navigate(['selectstudent']);
        }

      }

    } else {

      this.router.navigate(NavigationModel.getHomeURLPaths(['signin']));

    }

  }

  getStartedClicked() {

    if (UserModel.isLoggedIn()) {

      if (UserModel.getCurrent().hasSchoolAccess() && SchoolModel.getCurrent() === null) {

        this.router.navigate(['selectschool']);

      } else {

        if (StudentModel.getCurrent()) {
          this.router.navigate(['platform', 'dash']);
        } else {
          this.router.navigate(['selectstudent']);
        }

      }

    } else {

      this.router.navigate(NavigationModel.getHomeURLPaths(['signup']));

    }

  }

  navigateResearch() {

    this.router.navigate(NavigationModel.getHomeURLPaths(['research']));

  }

  getStartedButtonTitle(): string {

    if (UserModel.getCurrent()) {
      return 'header.dashboard';
    }

    return 'header.getStarted';
  }

  isUserLoggedIn(): boolean {

    if (UserModel.getCurrent()) {
      return true;
    }

    return false;
  }

}
